import React from "react";
import { ArrowLeft } from "lucide-react";

const SUPPORT_EMAIL = "support@marisdesk.com";
const HOME_URL = "https://marisdesk.com/home";

const LegalLayout = ({ title, lastUpdated, children, testid }) => (
  <div
    data-testid={testid}
    style={{ background: "var(--md-bg)" }}
    className="min-h-screen"
  >
    <header
      style={{
        background: "var(--md-bg)",
        borderBottom: "1px solid var(--md-line)",
      }}
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
        <a
          href={HOME_URL}
          className="font-display text-[1.4rem]"
          style={{ color: "var(--md-ink)" }}
          data-testid="legal-brand"
        >
          Maris Desk
        </a>
        <a
          href="/request-review"
          className="md-btn-ghost"
          data-testid="legal-back"
        >
          <ArrowLeft size={16} />
          Back to the review page
        </a>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14 sm:py-16">
      <p
        className="text-[0.72rem] tracking-[0.18em] uppercase font-semibold"
        style={{ color: "var(--md-blue)" }}
      >
        — Maris Desk · Guest Request Flow Review
      </p>
      <h1
        className="font-display mt-4 text-[2rem] sm:text-[2.4rem] leading-[1.1]"
        style={{ color: "var(--md-ink)" }}
        data-testid="legal-title"
      >
        {title}
      </h1>
      <p
        className="mt-3 text-sm"
        style={{ color: "var(--md-ink-mute)" }}
        data-testid="legal-last-updated"
      >
        Last updated: {lastUpdated}
      </p>
      <div
        className="mt-10 prose-md"
        style={{ color: "var(--md-ink-soft)" }}
        data-testid="legal-body"
      >
        {children}
      </div>
      <div
        className="mt-12 pl-5 py-2"
        style={{ borderLeft: "1px solid var(--md-blue)" }}
      >
        <p
          className="text-sm leading-relaxed"
          style={{ color: "var(--md-ink-soft)" }}
        >
          Questions about this page? Contact us at{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            style={{ color: "var(--md-blue)" }}
            data-testid="legal-support-email"
            className="hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </div>
    </main>

    <footer
      style={{
        background: "var(--md-bg-sand)",
        borderTop: "1px solid var(--md-line)",
      }}
      className="py-8"
    >
      <div
        className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-center gap-x-5 gap-y-2 text-xs flex-wrap"
        style={{ color: "var(--md-ink-mute)" }}
      >
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="hover:underline"
          style={{ color: "var(--md-blue)" }}
        >
          {SUPPORT_EMAIL}
        </a>
        <span aria-hidden style={{ opacity: 0.5 }}>·</span>
        <a
          href="/privacy"
          className="hover:underline"
          style={{ color: "var(--md-ink-soft)" }}
        >
          Privacy
        </a>
        <span aria-hidden style={{ opacity: 0.5 }}>·</span>
        <a
          href="/terms"
          className="hover:underline"
          style={{ color: "var(--md-ink-soft)" }}
        >
          Terms
        </a>
        <span aria-hidden style={{ opacity: 0.5 }}>·</span>
        <span>© {new Date().getFullYear()} Maris Desk</span>
      </div>
    </footer>

    {/* light prose styling */}
    <style>{`
      .prose-md h2 {
        font-family: 'Playfair Display', serif;
        font-size: 1.3rem;
        color: var(--md-ink);
        margin: 2rem 0 0.6rem 0;
        line-height: 1.25;
      }
      .prose-md p {
        font-size: 1rem;
        line-height: 1.65;
        margin: 0.8rem 0;
      }
      .prose-md ul {
        list-style: disc;
        padding-left: 1.2rem;
        margin: 0.6rem 0 1rem 0;
      }
      .prose-md li {
        margin: 0.35rem 0;
        line-height: 1.55;
      }
      .prose-md a {
        color: var(--md-blue);
      }
      .prose-md a:hover { text-decoration: underline; }
    `}</style>
  </div>
);

export const Privacy = () => (
  <LegalLayout
    title="Privacy"
    lastUpdated="June 2026"
    testid="privacy-page"
  >
    <p>
      This page explains how Maris Desk handles information submitted through
      the Guest Request Flow Review intake form at review.marisdesk.com.
    </p>

    <h2>What we collect</h2>
    <p>
      When you submit the Guest Request Flow Review form we collect only the
      information you provide:
    </p>
    <ul>
      <li>Your name</li>
      <li>Your property or hotel name</li>
      <li>Your email address</li>
      <li>Property type</li>
      <li>Website or booking page link (optional)</li>
      <li>Which channels guest requests come in through today (optional)</li>
      <li>Main concern you would like the review to look at</li>
      <li>Any free-text notes you include</li>
    </ul>
    <p>
      We do not collect payment information, government identifiers, or any
      data about your individual guests through this form.
    </p>

    <h2>How we use it</h2>
    <p>
      Your submission is used to prepare your Guest Request Flow Review and to
      follow up with you within 1–2 business days. We do not sell, share, or
      transfer your submission to third parties for marketing.
    </p>

    <h2>How it is stored</h2>
    <p>
      Submissions are stored in a private database accessible only to the
      Maris Desk team through an authenticated admin view. A notification
      email is sent to the Maris Desk team using a transactional email
      provider so that we can respond to your request.
    </p>

    <h2>Cookies and tracking</h2>
    <p>
      The Guest Request Flow Review page does not set marketing or analytics
      cookies. Only cookies strictly necessary to load the page may be set by
      your browser.
    </p>

    <h2>Your choices</h2>
    <p>
      You can request a copy of the information we hold about your
      submission, or ask us to delete it, at any time. Email us at{" "}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> with the request
      and we will action it.
    </p>

    <h2>Updates</h2>
    <p>
      We may update this page as the Guest Request Flow Review service
      evolves. The "Last updated" date at the top reflects the most recent
      revision.
    </p>
  </LegalLayout>
);

export const Terms = () => (
  <LegalLayout
    title="Terms of Use"
    lastUpdated="June 2026"
    testid="terms-page"
  >
    <p>
      These terms apply to your use of the Maris Desk Guest Request Flow
      Review page at review.marisdesk.com and to any review you request
      through it.
    </p>

    <h2>What the review is</h2>
    <p>
      The Guest Request Flow Review is a complimentary operational review for
      small hotels, villas, boutique stays, and short-term rental teams. It
      is informational. It does not establish a business relationship,
      contract, or fixed deliverable beyond a written summary shared with the
      requesting team.
    </p>

    <h2>What we expect from you</h2>
    <ul>
      <li>
        You will provide accurate information about your property when you
        submit the form.
      </li>
      <li>
        You will not use the form to submit content on behalf of someone else
        without their permission.
      </li>
      <li>
        You will not attempt to access non-public parts of the site (such as
        the admin lead viewer) without authorization.
      </li>
    </ul>

    <h2>No fees, no obligation</h2>
    <p>
      The Guest Request Flow Review is offered without setup, fees, or
      obligation. Receiving a review does not require you to purchase, sign
      up for, or commit to Maris Desk as a workspace.
    </p>

    <h2>Limitations</h2>
    <p>
      The review reflects observations and recommendations based on the
      information you share. It is not legal, tax, employment, or compliance
      advice. Maris Desk is not liable for decisions you make based on the
      review.
    </p>

    <h2>Changes</h2>
    <p>
      We may update or pause the Guest Request Flow Review offer at any time.
      Any active review requests already submitted will still be processed
      and followed up on.
    </p>

    <h2>Contact</h2>
    <p>
      Questions about these terms can be sent to{" "}
      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
    </p>
  </LegalLayout>
);
